import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injector, NgModule, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LibraryPanesModule } from '@app/library/panes/library-panes.module';
import { TutorMiniComponent } from '@app/library/panes/my-classes-tutors/tutor-mini/tutor-mini.component';
import { TeacherBookingSurfaceBlockComponent } from '@app/teachers/teacher-card/teacher-booking-surface-block/teacher-booking-surface-block.component';
import { ChapterComponent } from '@core/components/chapter/chapter.component';
import { EmptyComponent, EmptyProps } from '@core/components/empty/empty.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { NoContentStubComponent } from '@core/components/no-content-stub/no-content-stub.component';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { AccountService, FeaturesService, FilesService } from '@core/services';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { RatingComponent } from '@shared/rating/rating.component';
import { SkeletonComponent } from '@shared/skeleton/skeleton.component';
import { loadMe } from '@store/actions/profile.actions';
import { getMe } from '@store/reducers/profile.reducer';
import {
  EContentPaneFormat,
  FeatureEnum,
  IAccountStats,
  IContentPane,
  TenantEnum,
  User,
  UserStatusEnum,
} from 'lingo2-models';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { combineLatest, takeUntil } from 'rxjs';
import { AbstractPane } from '../models/abstract-pane';

const emptyProps: EmptyProps = {
  title: 'my-classes.no-tutors.title',
  text: 'my-classes.no-tutors.text',
  link: '/tutors',
  link_label: 'my-classes.no-tutors.link_label',
  link2: '/my-classes/tutors',
  image: 'tutors',
};

@Component({
  selector: 'app-my-classes-tutors',
  templateUrl: './my-classes-tutors.component.html',
  styleUrls: ['./my-classes-tutors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyClassesTutorsComponent extends AbstractPane implements OnInit, OnDestroy {
  public isShowClassrooms = false;
  public panesList: IContentPane[] = [];
  public selectedTutor: User;
  public isGuest: boolean;
  public me: User;
  public tenant: TenantEnum;
  public showEmptyContent: boolean;
  public emptyContent: Partial<EmptyProps>;
  public stats: Partial<IAccountStats>;
  public showSkeleton = true;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    protected features: FeaturesService,
    protected readonly store: Store,
    protected readonly inject: Injector,
  ) {
    super(inject);
  }

  protected onContentLoaded() {
    this.isVisible = true;
    this.showEmptyContent = !this.users.length && this.pane.display_mode !== 'row';
    this.showSkeleton = false;
    this.detectChanges();
  }

  public hasContent(): boolean {
    return true;
  }

  public get showSection() {
    return this.features.isAvailable(FeatureEnum.my_classes_tutors) || !AccountService.isAsIfTeacher(this.me);
  }

  ngOnInit() {
    combineLatest([this.store.select(getMe), this.features.tenant$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([me, tenant]) => {
          this.me = me;
          this.isGuest = me?.status === UserStatusEnum.guest;

          this.tenant = tenant;
          if (this.isMeTenant) {
            emptyProps.link_label = null;
          }
          this.detectChanges();
        },
        error: (error) => {
          this.store.dispatch(loadMe());
          this.tenant = TenantEnum.onclass_com;
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    this.emptyContent = emptyProps;
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public get isMeTenant() {
    return this.tenant === TenantEnum.onclass_me;
  }

  public onShowUser(user: User): void {
    this.isShowClassrooms = true;
    this.selectedTutor = user;
    this.stats = this.selectedTutor.stats;
    this.createdPanes(user.id);
    this.detectChanges();
  }

  public onBackToTeachers() {
    this.isShowClassrooms = false;
    this.selectedTutor = null;
    this.detectChanges();
  }

  public trackByFn(index: number, item: IContentPane): number {
    return item.order;
  }

  public get userFullName() {
    return this.selectedTutor.title;
  }

  public get userCover(): OnUiCover {
    return {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(this.selectedTutor.userpic_id),
      form: 'circle',
      link: this.tutorAccountUrl(),
    };
  }

  public tutorAccountUrl() {
    return AccountService.accountRoute(this.selectedTutor).join('/');
  }

  private createdPanes(id: string) {
    this.panesList = [
      {
        order: 0,
        format: EContentPaneFormat['my-classes-classrooms'],
        pagination: {
          page: 1,
          pageSize: 20,
          total: 0,
          totalPages: 0,
        },
        infinity: true,
        loaded: false,
        display_mode: 'row',
        hidden_filter_block: true,
        classroomsFilter: {
          author_id: id,
          include_archived: false,
          participated: true,
        },
      },
    ];
  }
}

@NgModule({
  declarations: [MyClassesTutorsComponent, TutorMiniComponent],
  imports: [
    CommonModule,
    SpinnersModule,
    RouterModule,
    TranslateModule,
    OnUiCoverModule,
    LibraryPanesModule,
    ModalModule,
    MobileModalModule,
    NoContentStubComponent,
    TeacherBookingSurfaceBlockComponent,
    ChapterComponent,
    EmptyComponent,
    RatingComponent,
    SkeletonComponent,
  ],
  exports: [MyClassesTutorsComponent, TutorMiniComponent],
})
export class MyClassesTutorsModule {}
